import { FC, useContext, useEffect, useRef } from 'react';
import { useLazyLoadQuery, useFragment, graphql } from 'react-relay';
import { useAuth } from '../hooks/useAuth';
import { useCheckoutSelector, useAppDispatch } from '../../../reducers/root';
import { buyerItemViewerFetchedAction } from '../../../actions/checkoutRelayAction';
import { usePrerenderedCheckoutStore } from '../../../prerender/prerenderedCheckoutStoreReact';
import { CheckoutServerVarsContext } from '../../shared/CheckoutServerVarsContext';
import CheckoutDeliveryOptionsQueryRenderer from './CheckoutDeliveryOptionsQueryRenderer';
import CheckoutPageHeader from '../../../header/CheckoutPageHeader';
import { CheckoutMobilePageHeader } from '../../../header/CheckoutMobilePageHeader';
import { PrerenderedCheckoutParams } from '../../../prerender/prerenderedCheckoutStore';
import { hasAnyTradeStatus } from '../../../helpers/checkoutUser';

import { CheckoutGuestBuyerQueryRenderer_buyer$key } from './__generated__/CheckoutGuestBuyerQueryRenderer_buyer.graphql';
import { CheckoutGuestBuyerQueryRenderer_viewer$key } from './__generated__/CheckoutGuestBuyerQueryRenderer_viewer.graphql';
import { CheckoutGuestBuyerQueryRenderer_item$key } from './__generated__/CheckoutGuestBuyerQueryRenderer_item.graphql';
import { CheckoutGuestBuyerQueryRenderer_emailUser$key } from './__generated__/CheckoutGuestBuyerQueryRenderer_emailUser.graphql';
import { CheckoutGuestBuyerQueryRenderer_preCheckoutTransaction$key } from './__generated__/CheckoutGuestBuyerQueryRenderer_preCheckoutTransaction.graphql';
import { CheckoutGuestBuyerQueryRendererClientQuery } from './__generated__/CheckoutGuestBuyerQueryRendererClientQuery.graphql';

type Props = {
    // this buyer prop is not used in this component, we get the same data from
    // the client-only query used in useLazyLoadQuery. Keep this prop here to ensure the
    // parent component fetches the data
    buyer: CheckoutGuestBuyerQueryRenderer_buyer$key | null;
    viewer: CheckoutGuestBuyerQueryRenderer_viewer$key | null;
    item: CheckoutGuestBuyerQueryRenderer_item$key | null;
    emailUser: CheckoutGuestBuyerQueryRenderer_emailUser$key | null;
    preCheckoutTransaction: CheckoutGuestBuyerQueryRenderer_preCheckoutTransaction$key | null;
};

const CheckoutGuestBuyerQueryRenderer: FC<Props> = ({
    viewer: viewerRef,
    item: itemRef,
    emailUser: emailUserRef,
    preCheckoutTransaction: preCheckoutTransactionRef,
}) => {
    const dispatch = useAppDispatch();
    const buyerId = useCheckoutSelector(state => state.transaction.buyerId);
    const fetchedBuyerId = useCheckoutSelector(state => state.transaction.fetchedBuyerId);
    const { isMobile } = useContext(CheckoutServerVarsContext);
    const processedPrerenderedParamsRef = useRef<PrerenderedCheckoutParams | null>();
    const viewer = useFragment(
        graphql`
            fragment CheckoutGuestBuyerQueryRenderer_viewer on Viewer {
                ...CheckoutPageHeader_viewer @skip(if: $isMobile)
                ...CheckoutDeliveryOptionsQueryRenderer_viewer
                ...useAuth_viewer
                ...checkoutRelayAction_viewer
                ...CheckoutMobilePageHeader_viewer @include(if: $isMobile)
            }
        `,
        viewerRef
    );

    const item = useFragment(
        graphql`
            fragment CheckoutGuestBuyerQueryRenderer_item on Item {
                ...CheckoutDeliveryOptionsQueryRenderer_item
                ...checkoutRelayAction_item
                ...CheckoutMobilePageHeader_item
            }
        `,
        itemRef
    );

    const preCheckoutTransaction = useFragment(
        graphql`
            fragment CheckoutGuestBuyerQueryRenderer_preCheckoutTransaction on Transaction {
                ...CheckoutDeliveryOptionsQueryRenderer_preCheckoutTransaction
                ...checkoutRelayAction_preCheckoutTransaction
            }
        `,
        preCheckoutTransactionRef
    );

    const emailUser = useFragment<CheckoutGuestBuyerQueryRenderer_emailUser$key>(
        graphql`
            fragment CheckoutGuestBuyerQueryRenderer_emailUser on User {
                ...checkoutRelayAction_emailUser
            }
        `,
        emailUserRef
    );

    const hasTradeStatus =
        useCheckoutSelector(state => state.tradeStatus.hasTradeStatus) || hasAnyTradeStatus();

    const clientData = useLazyLoadQuery<CheckoutGuestBuyerQueryRendererClientQuery>(
        graphql`
            query CheckoutGuestBuyerQueryRendererClientQuery(
                $buyerId: String = ""
                $hasBuyerId: Boolean!
                $isMobile: Boolean!
                $hasTradeStatus: Boolean!
            ) {
                viewer {
                    user(userId: $buyerId) @include(if: $hasBuyerId) {
                        ...CheckoutGuestBuyerQueryRenderer_buyer
                    }
                }
            }
        `,
        {
            buyerId,
            hasBuyerId: !!buyerId,
            isMobile,
            hasTradeStatus,
        },
        { fetchPolicy: 'store-only' }
    );

    const buyer = useFragment<CheckoutGuestBuyerQueryRenderer_buyer$key>(
        graphql`
            fragment CheckoutGuestBuyerQueryRenderer_buyer on User {
                ...CheckoutDeliveryOptionsQueryRenderer_buyer
                ...CheckoutPageHeader_buyer @skip(if: $isMobile)
                ...CheckoutMobilePageHeader_buyer @include(if: $isMobile)
                ...checkoutRelayAction_buyer
                ...useAuth_user
                serviceId
            }
        `,
        clientData.viewer?.user || null
    );

    const { params } = usePrerenderedCheckoutStore();
    useEffect(() => {
        if (
            viewer &&
            (!buyer ||
                buyer.serviceId !== fetchedBuyerId ||
                processedPrerenderedParamsRef.current !== params)
        ) {
            dispatch(
                buyerItemViewerFetchedAction({
                    viewer,
                    item,
                    buyer,
                    emailUser,
                    preCheckoutTransaction,
                })
            );
            processedPrerenderedParamsRef.current = params;
        }
        // store will be updated if:
        // 1) data changed
        // 2) prerendered checkout params changed
        // prerendered checkout params are taken from url params
    }, [dispatch, params, viewer, item, buyer, preCheckoutTransaction, fetchedBuyerId, emailUser]);

    useAuth({ user: buyer, viewer });

    return (
        <>
            {isMobile ? (
                <CheckoutMobilePageHeader viewer={viewer} buyer={buyer} item={item} />
            ) : (
                <CheckoutPageHeader viewer={viewer} buyer={buyer} />
            )}
            <CheckoutDeliveryOptionsQueryRenderer
                viewer={viewer}
                item={item}
                buyer={buyer}
                preCheckoutTransaction={preCheckoutTransaction}
            />
        </>
    );
};

export default CheckoutGuestBuyerQueryRenderer;
